import * as React from "react";
import {
  UsersTableContainer,
  Heading,
  HeadingLine,
  StateInfo,
  Filters,
  SearchInputContainer,
  SearchInput,
  CustomCheckBox,
} from "./UsersPage.styles";
import { FieldSet } from "../../../../App/Common/Components/FormComponents";
import { SectionContainer } from "../common/Charts.styles";
import { NotStartedIcon, InProgressIcon, CompletedIcon } from "./UsersPageIcons";
import { IUser, IUsersAnalytics, IUserAnalytics } from "../../../../App/Analytics/Common";
import { lastSeenValue, timeTakenValue } from "../../../../formatters/users";
import UsersPageTable, { IUserRow } from "./UsersPageTable";
import { OrderingColumn, OrderingDirection } from "ts/components/utils/Pagination/NumberedPagination/table";
import { PageContextProvider } from "../../../../context/numbered-pagination";
import { usePagination } from "../../../utils/Pagination/hooks";
import { BottomPagination, Count, TopPagination } from "../../../utils/Pagination/NumberedPagination/common";
import { pluralize } from "../../../../utils";

interface IUsersTableProps {
  nameFilter: string;
  useSelectedAsFilter: boolean;
  users: IUser[];
  selected: Set<string>;
  selectedFromGraph: Set<string>;
  usersAnalytics: IUsersAnalytics;
  onToggleSelectAll: () => void;
  onToggleSelect: (accountId: string) => () => void;
  onUseSelectedAsFilterChange: () => void;
  onNameFilterChange: (e: any) => void;
  showFilteredTable: boolean;
}

interface IUsersTableState {
  ordering: [OrderingColumn, OrderingDirection];
}

const lastSeenScore = (u1: IUser, checked: Set<string>, userAnalytics: IUsersAnalytics) => {
  if (userAnalytics[u1.accountId] === undefined) {
    return 1;
  } else if (userAnalytics[u1.accountId].lastSeen === null) {
    return 1;
  } else {
    return 2;
  }
};

const lastStateScore = (u1: IUser, checked: Set<string>, userAnalytics: IUsersAnalytics) => {
  if (userAnalytics[u1.accountId] === undefined) {
    return 1;
  } else if (userAnalytics[u1.accountId].lastSeen === null) {
    return 1;
  } else if (userAnalytics[u1.accountId].completed) {
    return 3;
  } else {
    return 2;
  }
};

export const comparators: {
  [k in OrderingColumn]: (u1: IUser, u2: IUser, checked: Set<string>, userAnalytics: IUsersAnalytics) => number;
} = {
  checked: (u1, u2, checked, userAnalytics) => {
    if (checked.has(u1.accountId) && !checked.has(u2.accountId)) {
      return 1;
    } else if (!checked.has(u2.accountId) && checked.has(u2.accountId)) {
      return -1;
    } else {
      return 0;
    }
  },
  dateLastSeen: (u1, u2, checked, userAnalytics) => {
    if (lastSeenScore(u1, checked, userAnalytics) < lastSeenScore(u2, checked, userAnalytics)) {
      return 1;
    } else if (lastSeenScore(u1, checked, userAnalytics) > lastSeenScore(u2, checked, userAnalytics)) {
      return -1;
    } else {
      const lastSeen1 = userAnalytics[u1.accountId] === undefined ? 0 : userAnalytics[u1.accountId].lastSeen || 0;
      const lastSeen2 = userAnalytics[u2.accountId] === undefined ? 0 : userAnalytics[u2.accountId].lastSeen || 0;
      if (lastSeen1 < lastSeen2) {
        return 1;
      } else if (lastSeen1 > lastSeen2) {
        return -1;
      } else {
        return 0;
      }
    }
  },
  learnerName: (u1, u2, checked, userAnalytics) =>
    u1.displayName.toLowerCase() === u2.displayName.toLowerCase()
      ? 0
      : u1.displayName.toLowerCase() > u2.displayName.toLowerCase()
      ? 1
      : -1,
  resets: (u1, u2, checked, userAnalytics) => {
    if (userAnalytics[u1.accountId] === undefined && userAnalytics[u2.accountId] === undefined) {
      return 0;
    } else if (userAnalytics[u1.accountId] === undefined && userAnalytics[u2.accountId] !== undefined) {
      return 1;
    } else if (userAnalytics[u1.accountId] !== undefined && userAnalytics[u2.accountId] === undefined) {
      return -1;
    } else if (userAnalytics[u1.accountId].resets < userAnalytics[u2.accountId].resets) {
      return 1;
    } else if (userAnalytics[u1.accountId].resets > userAnalytics[u2.accountId].resets) {
      return -1;
    } else {
      return 0;
    }
  },
  state: (u1, u2, checked, userAnalytics) => {
    if (lastStateScore(u1, checked, userAnalytics) < lastStateScore(u2, checked, userAnalytics)) {
      return 1;
    } else if (lastStateScore(u1, checked, userAnalytics) > lastStateScore(u2, checked, userAnalytics)) {
      return -1;
    } else {
      return 0;
    }
  },
  time: (u1, u2, checked, userAnalytics) => {
    if (userAnalytics[u1.accountId] === undefined && userAnalytics[u2.accountId] === undefined) {
      return 0;
    } else if (userAnalytics[u1.accountId] === undefined && userAnalytics[u2.accountId] !== undefined) {
      return 1;
    } else if (userAnalytics[u1.accountId] !== undefined && userAnalytics[u2.accountId] === undefined) {
      return -1;
    } else if (userAnalytics[u1.accountId].time < userAnalytics[u2.accountId].time) {
      return 1;
    } else if (userAnalytics[u1.accountId].time > userAnalytics[u2.accountId].time) {
      return -1;
    } else {
      return 0;
    }
  },
  topics: (u1, u2, checked, userAnalytics) => {
    if (userAnalytics[u1.accountId] === undefined && userAnalytics[u2.accountId] === undefined) {
      return 0;
    } else if (userAnalytics[u1.accountId] === undefined && userAnalytics[u2.accountId] !== undefined) {
      return 1;
    } else if (userAnalytics[u1.accountId] !== undefined && userAnalytics[u2.accountId] === undefined) {
      return -1;
    } else if (userAnalytics[u1.accountId].teachings < userAnalytics[u2.accountId].teachings) {
      return 1;
    } else if (userAnalytics[u1.accountId].teachings > userAnalytics[u2.accountId].teachings) {
      return -1;
    } else {
      return 0;
    }
  },
};

const UsersPage: React.FC<IUsersTableProps> = (props: IUsersTableProps) => {
  const [state, setState] = React.useState<IUsersTableState>({ ordering: ["learnerName", "asc"] });
  const onToggleSort = (column: OrderingColumn) => () => {
    const ordering: [OrderingColumn, OrderingDirection] =
      state.ordering[0] === column
        ? [column, state.ordering[1] === "asc" ? "desc" : "asc"]
        : [column, "asc" as OrderingDirection];

    setState({ ordering });
  };

  const filteredUsers = (() => {
    if (props.nameFilter.trim()) {
      return props.users.filter(u => u.displayName.toLowerCase().indexOf(props.nameFilter.trim().toLowerCase()) > -1);
    }
    return props.users;
  })();

  const { selected, selectedFromGraph } = props;
  const conditionallyFilteredUsers =
    selected.size === 0 && selectedFromGraph.size === 0
      ? filteredUsers
      : filteredUsers.filter(u => selected.has(u.accountId) || selectedFromGraph.has(u.accountId));

  const filteredAccountIds = new Set(conditionallyFilteredUsers.map(u => u.accountId));
  const comparator = comparators[state.ordering[0]];

  const sortedUsers = (() => {
    const sorted = (!props.showFilteredTable ? conditionallyFilteredUsers : filteredUsers).sort((u1, u2) =>
      comparator(u1, u2, props.selected, props.usersAnalytics)
    );
    if (state.ordering[1] === "desc") {
      return sorted.slice().reverse();
    }
    return sorted;
  })();

  const toTableUser = (sortedUser: IUser, sortedUserAnalytics: IUserAnalytics): IUserRow => ({
    checked: (
      <CustomCheckBox
        type="checkbox"
        onChange={props.onToggleSelect(sortedUser.accountId)}
        checked={props.selected.has(sortedUser.accountId)}
        data-testid="user-checkbox"
        name="user-checkbox"
      />
    ),
    learnerName: sortedUser.displayName,
    state: (() => {
      if (sortedUserAnalytics === undefined) return <NotStartedIcon />;
      if (sortedUserAnalytics.completed) return <CompletedIcon />;
      if (sortedUserAnalytics.lastSeen !== null) return <InProgressIcon />;
      return <NotStartedIcon />;
    })(),
    /* tslint:disable-next-line */
    dateLastSeen: lastSeenValue(sortedUserAnalytics),
    resets: sortedUserAnalytics === undefined ? 0 : sortedUserAnalytics.resets,
    time: timeTakenValue(sortedUserAnalytics),
    topics: sortedUserAnalytics === undefined ? 0 : sortedUserAnalytics.teachings,
  });

  const checkIfFilteredRow = (u: IUser) => {
    return (
      (props.useSelectedAsFilter && !filteredAccountIds.has(u.accountId)) ||
      (props.selectedFromGraph.size > 0 && !props.selectedFromGraph.has(u.accountId))
    );
  };

  const isFilterActive = selected.size > 0;
  const isCheckBoxActive = props.useSelectedAsFilter && isFilterActive;

  const total = sortedUsers.length;
  const { value, pagedData } = usePagination(sortedUsers, 10);

  const data = pagedData.map(u => {
    const userAnalytics = props.usersAnalytics[u.accountId];
    return toTableUser(u, userAnalytics);
  });
  const isFilteredOutRow = pagedData.map(checkIfFilteredRow);
  const sortingFns = [
    props.onToggleSelectAll,
    onToggleSort("learnerName"),
    onToggleSort("state"),
    onToggleSort("dateLastSeen"),
    onToggleSort("resets"),
    onToggleSort("time"),
    onToggleSort("topics"),
  ];

  return (
    <UsersTableContainer data-testid="users-table-container">
      <PageContextProvider value={value}>
        <Heading data-testid="users-heading-container">
          <SectionContainer>
            <Filters>
              <FieldSet>
                <legend>Learner Name Search</legend>
                <SearchInputContainer>
                  <SearchInput
                    type="text"
                    value={props.nameFilter}
                    onChange={props.onNameFilterChange}
                    placeholder="Type here"
                  />
                </SearchInputContainer>
              </FieldSet>
              <div>
                <CustomCheckBox
                  type="checkbox"
                  data-testid="use-selected-checkbox"
                  checked={isCheckBoxActive}
                  onChange={props.onUseSelectedAsFilterChange}
                  disabled={!isFilterActive}
                />
                <span>Use selected learners as filter criterion</span>
              </div>
            </Filters>
          </SectionContainer>
          <HeadingLine>
            <StateInfo>
              State:
              {"  "}
              <NotStartedIcon /> Not Started
              {"  "}
              <InProgressIcon /> In progress
              {"  "}
              <CompletedIcon /> Completed
            </StateInfo>
            <TopPagination total={total} />
            <Count>{pluralize("Learner", props.users.length)}</Count>
          </HeadingLine>
        </Heading>
        <UsersPageTable
          data={data}
          sortingFns={sortingFns}
          isFilteredOutRow={isFilteredOutRow}
          ordering={state.ordering}
          allSelected={props.users.length === props.selected.size}
        />
        <BottomPagination total={total} />
      </PageContextProvider>
    </UsersTableContainer>
  );
};

export default UsersPage;
